import React from 'react';

/**
 * Extracts plain text from React JSX elements recursively.
 *
 * Safety considerations:
 * - Guards against excessively deep trees via MAX_DEPTH
 * - Tracks visited objects to avoid infinite loops on cyclic structures
 */
const MAX_DEPTH = 10;

const innerExtract = (
  element: React.ReactNode,
  depth: number,
  visited: WeakSet<object>,
): string => {
  if (depth > MAX_DEPTH) {
    return '';
  }

  if (typeof element === 'string') {
    return element;
  }
  if (typeof element === 'number') {
    return String(element);
  }
  if (Array.isArray(element)) {
    return element.map((child) => innerExtract(child, depth + 1, visited)).join(' ');
  }

  if (element && typeof element === 'object') {
    if (visited.has(element as object)) {
      // Prevent infinite recursion in case of unexpected cycles
      return '';
    }
    visited.add(element as object);

    if ('props' in element) {
      const props = (element as { props?: unknown }).props as {
        children?: React.ReactNode;
        // For react-intl <FormattedMessage> or similar components
        defaultMessage?: string;
        id?: string;
      };

      // Prefer explicit defaultMessage when available (works well with FormattedMessage)
      if (typeof props?.defaultMessage === 'string' && props.defaultMessage.trim()) {
        return props.defaultMessage.trim();
      }

      if (props?.children) {
        const childText = innerExtract(props.children, depth + 1, visited);
        if (childText.trim()) {
          return childText;
        }
      }
    }
  }

  return '';
};

export const extractTextFromJSX = (element: React.ReactNode): string => {
  return innerExtract(element, 0, new WeakSet<object>());
};

